/**
 * SchoolsTrashPanel — Papelera de escuelas para el Super Admin.
 *
 * Muestra escuelas soft-deleted (< 7 días).
 * - Restaurar: reactiva la escuela y sus usuarios.
 * - Purgar: hard-delete inmediato con confirmación por nombre.
 * Auto-purga en backend cualquier escuela con > 7 días en papelera.
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Trash2, Undo2, RefreshCw, Loader2, AlertTriangle, Archive,
} from "lucide-react";

function PurgeDialog({ school, onPurged }) {
  const [open, setOpen] = useState(false);
  const [confirmName, setConfirmName] = useState("");
  const [purging, setPurging] = useState(false);

  useEffect(() => { if (!open) setConfirmName(""); }, [open]);

  const nameMatches = confirmName.trim() === (school.name || "").trim();

  const doPurge = async () => {
    if (!nameMatches) return;
    setPurging(true);
    try {
      await axios.delete(`${API}/admin/schools-v2/${school.id}/purge`, {
        params: { confirm_name: confirmName.trim() },
        withCredentials: true,
      });
      toast.success(`"${school.name}" fue eliminada permanentemente`);
      setOpen(false);
      if (onPurged) onPurged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo purgar la escuela");
    } finally {
      setPurging(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="text-red-600 hover:text-red-700 hover:bg-red-50 gap-1"
          data-testid={`purge-school-${school.id}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
          Purgar ahora
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="max-w-md" data-testid="purge-school-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="w-5 h-5" />
            Eliminar permanentemente
          </AlertDialogTitle>
          <AlertDialogDescription>
            Vas a eliminar de forma <b>definitiva e irreversible</b> la escuela{" "}
            <b className="text-gray-900">{school.name}</b> y todos sus datos vinculados
            (usuarios, evidencias, mensajes, etc.).
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            Escribe <span className="font-mono text-red-700">{school.name}</span> para confirmar:
          </label>
          <Input
            value={confirmName}
            onChange={(e) => setConfirmName(e.target.value)}
            placeholder={school.name}
            disabled={purging}
            autoComplete="off"
            data-testid="purge-school-confirm-input"
          />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={purging}>Cancelar</AlertDialogCancel>
          <AlertDialogAction asChild disabled={!nameMatches || purging}>
            <Button
              onClick={(e) => { e.preventDefault(); doPurge(); }}
              disabled={!nameMatches || purging}
              className="bg-red-600 hover:bg-red-700 text-white gap-1.5"
              data-testid="purge-school-confirm-btn"
            >
              {purging ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              Eliminar para siempre
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default function SchoolsTrashPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [restoringId, setRestoringId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/schools-v2/trash`, { withCredentials: true });
      setData(res.data);
      if (res.data.auto_purged_count > 0) {
        toast.info(`Se purgaron automáticamente ${res.data.auto_purged_count} escuela(s) con más de 7 días.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar la papelera");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const restore = async (school) => {
    if (!window.confirm(`¿Restaurar "${school.name}"?\n\nLos ${school.users_frozen} usuario(s) volverán a poder iniciar sesión.`)) return;
    setRestoringId(school.id);
    try {
      await axios.post(`${API}/admin/schools-v2/${school.id}/restore`, {}, { withCredentials: true });
      toast.success(`"${school.name}" restaurada`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo restaurar");
    } finally {
      setRestoringId(null);
    }
  };

  const rows = data?.schools || [];

  return (
    <div className="space-y-4" data-testid="schools-trash-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Archive className="w-5 h-5 text-amber-600" /> Papelera de escuelas
          </h2>
          <p className="text-sm text-gray-500">
            Las escuelas permanecen 7 días en papelera antes de purgarse automáticamente.
            Restaurarlas reactiva todos sus usuarios.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="trash-refresh">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          <span className="ml-1">Actualizar</span>
        </Button>
      </div>

      {loading && !data && (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando…
        </div>
      )}

      {data && rows.length === 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
          <Archive className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h3 className="font-bold text-gray-700 mb-1">Papelera vacía</h3>
          <p className="text-sm text-gray-500">
            Cuando muevas una escuela a la papelera aparecerá aquí durante 7 días.
          </p>
        </div>
      )}

      {rows.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="divide-y divide-gray-100">
            {rows.map((s) => {
              const danger = s.days_remaining < 2;
              return (
                <div key={s.id} className="p-4 flex flex-col md:flex-row md:items-center gap-3" data-testid={`trash-row-${s.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-gray-900 truncate">{s.name}</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Movida por <b>{s.deleted_by_name || "—"}</b>
                      {s.deleted_at && ` · ${new Date(s.deleted_at).toLocaleString("es")}`}
                    </p>
                    <div className="flex gap-2 mt-1.5 flex-wrap">
                      <span className="text-xs text-gray-500">
                        <b>{s.users_frozen}</b> usuario(s) congelado(s)
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        danger
                          ? "bg-red-50 text-red-700 border border-red-200"
                          : "bg-amber-50 text-amber-700 border border-amber-200"
                      }`}>
                        {s.days_remaining > 0
                          ? `${s.days_remaining} día(s) restantes`
                          : "Se purgará pronto"}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => restore(s)}
                      disabled={restoringId === s.id}
                      className="text-green-700 border-green-200 hover:bg-green-50 gap-1"
                      data-testid={`restore-school-${s.id}`}
                    >
                      {restoringId === s.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Undo2 className="w-3.5 h-3.5" />}
                      Restaurar
                    </Button>
                    <PurgeDialog school={s} onPurged={load} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
